import type { Texture } from 'three';
import {
  abs,
  exp,
  float,
  length,
  max,
  mix,
  pow,
  smoothstep,
  texture,
  uv,
  vec2,
  vec3,
} from 'three/tsl';

import { ENGINE_BACKDROP_CONFIG } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop/config';
import { groundProjection } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop/dot-grid-ground/groundProjection';
import { fbm } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop/gradientNoise';

// A short impulse travelling across the SAME projected floor as the dots.
// The photo breaks up the wave front; no rising fire or ambient burning layer.
export const createPowerBurstNodes = (
  time: any,
  aspect: any,
  origin: any,
  engineSize: any,
  fire: Texture
) => {
  const { projection: authored, grid } = ENGINE_BACKDROP_CONFIG.ground;
  const projection = {
    aspect,
    pitch: float(authored.pitch),
    rollRad: float((authored.rollDeg * Math.PI) / 180),
    horizon: float(authored.horizon),
    horizonFeather: float(authored.horizonFeather),
    originX: float(authored.panX),
    originY: float(authored.panY),
    scale: float(authored.scale),
  };
  const floor = groundProjection(projection, grid.compression);
  const center = groundProjection(projection, grid.compression, origin);
  // Expand the ground wave and its textured wake by 30% around the engine.
  const p = floor.coord.sub(center.coord).div(1.3);
  const radius = length(p);
  const direction = p.div(max(radius, 0.001));
  const distortion = fbm(p.mul(7).add(vec2(time.mul(0.3), 0)), 3);
  // Quick impact expansion transitions into a slower travelling wave.
  const frontRadius = time
    .mul(0.35)
    .add(exp(time.mul(-10)).oneMinus().mul(0.3))
    .add(0.09);
  const front = exp(
    abs(radius.add(distortion.mul(0.09)).sub(frontRadius)).mul(-26)
  );
  const wake = exp(abs(radius.sub(frontRadius.mul(0.8))).mul(-18)).mul(0.24);
  const sampled = texture(fire, p.mul(2.6).sub(direction.mul(time).mul(0.35)));
  const detail = pow(sampled.g.mul(0.7).add(sampled.r.mul(0.3)), 1.3);
  const color = mix(vec3(1, 0.025, 0.001), vec3(1, 0.14, 0.006), detail);
  // Protect the engine's measured box; energy belongs around it, not on it.
  const screenRadius = length(uv().sub(origin).mul(vec2(aspect, 1)).mul(2));
  const clearEngine = smoothstep(
    engineSize.mul(0.7),
    engineSize.mul(1.05),
    screenRadius
  );
  // Hold the travelling front long enough to read its outward movement,
  // then release it before the next 4.8-second power pulse.
  const decay = exp(time.mul(-0.25)).mul(smoothstep(1.8, 2.4, time).oneMinus());
  const edge = smoothstep(0, 0.08, uv().x)
    .mul(smoothstep(0.92, 1, uv().x).oneMinus())
    .mul(smoothstep(0, 0.1, uv().y))
    .mul(smoothstep(0.88, 1, uv().y).oneMinus());
  const energy = detail
    .mul(0.85)
    .add(0.15)
    .mul(front.add(wake))
    .mul(decay)
    .mul(clearEngine)
    .mul(floor.mask)
    .mul(edge)
    .mul(8);
  // Compress intensity BEFORE tinting: channel clipping would turn hot orange
  // yellow/white. Keep the visible wake, but cap emitted light at the crest.
  const intensity = exp(energy.negate()).oneMinus().mul(1.25);
  return color.mul(intensity);
};
